import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { purchasesApi } from '../api/purchases';
import { partiesApi } from '../api/parties';
import { productsApi } from '../api/products';
import type { PurchaseInvoice, PurchaseInvoiceItem, Party, Product } from '@coreledger/shared-types';
import { calcItemTotals, gstBreakdown } from '@coreledger/business-logic';
import Pagination from '../components/Pagination';
import { confirm } from '../components/ConfirmDialog';
import RecordPurchasePaymentModal from '../components/RecordPurchasePaymentModal';
import { useRole } from '../context/AuthContext';

const GST_RATES = [0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 28];

const TDS_SECTIONS: { section: string; label: string; rate: number }[] = [
  { section: '192',            label: '192 — Salaries',                                         rate: 0    },
  { section: '193',            label: '193 — Interest on Securities',                            rate: 10   },
  { section: '194',            label: '194 — Dividends',                                         rate: 10   },
  { section: '393(1)(ca)',     label: '393(1)(ca) — Interest [prev. 194A]',                      rate: 10   },
  { section: '393(1)(f)',      label: '393(1)(f) — Contractors [prev. 194C]',                    rate: 2    },
  { section: '393(1)(g)',      label: '393(1)(g) — Insurance Commission [prev. 194D]',           rate: 5    },
  { section: '393(1)(j)',      label: '393(1)(j) — Commission on Lottery [prev. 194G]',          rate: 5    },
  { section: '393(1)(k)',      label: '393(1)(k) — Commission / Brokerage [prev. 194H]',         rate: 5    },
  { section: '393(1)(l)(i)',   label: '393(1)(l)(i) — Rent: Machinery/Equipment [prev. 194I]',  rate: 2    },
  { section: '393(1)(l)(ii)',  label: '393(1)(l)(ii) — Rent: Land / Building [prev. 194I]',     rate: 10   },
  { section: '393(1)(m)',      label: '393(1)(m) — Immovable Property Transfer [prev. 194IA]',  rate: 1    },
  { section: '393(1)(n)',      label: '393(1)(n) — Professional / Technical Services [prev. 194J]', rate: 10 },
  { section: '393(1)(o)',      label: '393(1)(o) — E-Commerce / Online Ads [prev. 194O]',       rate: 1    },
  { section: '393(1)(p)',      label: '393(1)(p) — Purchase of Goods [prev. 194Q]',             rate: 0.1  },
  { section: '393(1)(s)',      label: '393(1)(s) — Cash Withdrawal [prev. 194N]',               rate: 2    },
  { section: '195',            label: '195 — Payments to Non-Residents',                        rate: 20   },
];

const fmt = (n: number) => '₹' + (n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmt2 = (n: number) => (n ?? 0).toFixed(2);
const today = () => new Date().toISOString().slice(0, 10);
const uid = () => Math.random().toString(36).slice(2);

function emptyItem(): PurchaseInvoiceItem {
  return { id: uid(), description: '', qty: 1, rate: 0, taxableValue: 0, gstRate: 18, cgst: 0, sgst: 0, igst: 0, total: 0 };
}

function calcItem(item: PurchaseInvoiceItem, taxType: string): PurchaseInvoiceItem {
  const { taxableValue, gstAmount, total } = calcItemTotals(item);
  const { cgst, sgst, igst } = gstBreakdown(taxType, gstAmount);
  return { ...item, taxableValue, cgst, sgst, igst, total };
}

function emptyPurchase(): Partial<PurchaseInvoice> {
  return {
    date: today(), taxType: 'cgst_sgst', status: 'draft', paymentStatus: 'unpaid',
    docType: 'purchase_invoice',
    items: [emptyItem()], subtotal: 0, taxTotal: 0, total: 0, journalEntries: [],
  };
}

export default function PurchasesPage() {
  const { canDelete } = useRole();
  const qc = useQueryClient();
  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list');
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<PurchaseInvoice>>(emptyPurchase());
  const [partySearch, setPartySearch] = useState('');
  const [showPartyDD, setShowPartyDD] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [payModal, setPayModal] = useState<string | null>(null); // purchaseId
  const [viewing, setViewing] = useState<PurchaseInvoice | null>(null);
  const [page, setPage] = useState(1);
  const [linkedSearch, setLinkedSearch] = useState('');
  const [showLinkedDD, setShowLinkedDD] = useState(false);
  const [showInlineParty, setShowInlineParty] = useState(false);
  const [inlinePartyForm, setInlinePartyForm] = useState({ name: '', gstin: '', type: 'vendor' as 'customer' | 'vendor' | 'both' });
  const [inlinePartySaving, setInlinePartySaving] = useState(false);
  const [inlineProductIdx, setInlineProductIdx] = useState<number | null>(null);
  const [inlineProductForm, setInlineProductForm] = useState({ name: '', hsnSac: '', unit: 'pcs', rate: 0, gstRate: 18 });
  const [inlineProductSaving, setInlineProductSaving] = useState(false);
  const [prodDropIdx, setProdDropIdx] = useState<number | null>(null);

  const { data: purchasesData, isLoading } = useQuery({ queryKey: ['purchases', page], queryFn: () => purchasesApi.list(page, 50), staleTime: 30 * 1000 });
  const purchases = purchasesData?.data ?? [];
  const purchasesMeta = purchasesData?.meta;
  const { data: partiesData } = useQuery({ queryKey: ['parties'], queryFn: () => partiesApi.list(), staleTime: 30 * 1000 });
  const parties = partiesData?.data ?? [];
  const { data: productsData } = useQuery({ queryKey: ['products'], queryFn: () => productsApi.list(), staleTime: 30 * 1000 });
  const products = productsData?.data ?? [];

  const del = useMutation({ mutationFn: purchasesApi.delete, onSuccess: () => { qc.invalidateQueries({ queryKey: ['purchases'] }); qc.refetchQueries({ type: 'all' }); } });

  const filtered = purchases.filter(p => {
    const q = listSearch.toLowerCase();
    return !q || p.partyName.toLowerCase().includes(q) || (p.invoiceNumber ?? '').toLowerCase().includes(q) || (p.supplierInvoice ?? '').toLowerCase().includes(q);
  });

  function openCreate() {
    setForm(emptyPurchase()); setPartySearch(''); setLinkedSearch(''); setEditing(null); setError(null); setMode('create');
  }
  function openEdit(p: PurchaseInvoice) {
    setForm({ ...p, items: p.items.map(it => ({ ...it })) }); setPartySearch(p.partyName);
    const linked = purchases.find(x => x.id === p.linkedPurchaseId);
    setLinkedSearch(linked ? linked.invoiceNumber : '');
    setEditing(p.id); setError(null); setMode('edit');
  }
  function backToList() { setMode('list'); setError(null); }

  function recalcTotals(items: PurchaseInvoiceItem[]) {
    const subtotal = items.reduce((s, it) => s + it.taxableValue, 0);
    const taxTotal = items.reduce((s, it) => s + it.cgst + it.sgst + it.igst, 0);
    return { items, subtotal, taxTotal, total: subtotal + taxTotal };
  }

  function setItem(idx: number, k: keyof PurchaseInvoiceItem, val: string | number) {
    setForm(f => {
      const taxType = f.taxType ?? 'cgst_sgst';
      const items = [...(f.items ?? [])];
      const updated = calcItem({ ...items[idx], [k]: typeof val === 'string' && k !== 'description' && k !== 'hsnSac' ? parseFloat(val) || 0 : val }, taxType);
      items[idx] = updated;
      return { ...f, ...recalcTotals(items) };
    });
  }

  function addItem() { setForm(f => ({ ...f, items: [...(f.items ?? []), emptyItem()] })); }
  function removeItem(idx: number) {
    setForm(f => {
      const items = (f.items ?? []).filter((_, i) => i !== idx);
      return { ...f, ...recalcTotals(items) };
    });
  }

  const setTaxType = useCallback((tt: 'cgst_sgst' | 'igst' | 'exempt') => {
    setForm(f => {
      const items = (f.items ?? []).map(it => calcItem(it, tt));
      return { ...f, taxType: tt, ...recalcTotals(items) };
    });
  }, []);

  function selectParty(p: Party) {
    setForm(f => ({ ...f, partyId: p.id, partyName: p.name, partyGstin: p.gstin }));
    setPartySearch(p.name); setShowPartyDD(false);
  }

  function fillFromProduct(idx: number, p: typeof products[0]) {
    setForm(f => {
      const taxType = f.taxType ?? 'cgst_sgst';
      const items = [...(f.items ?? [])];
      items[idx] = calcItem({ ...items[idx], productId: p.id, description: p.name, hsnSac: p.hsnSac, rate: p.rate, gstRate: p.gstRate }, taxType);
      return { ...f, ...recalcTotals(items) };
    });
  }

  const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i;

  async function handleSave(status: 'draft' | 'confirmed') {
    if (!form.partyName?.trim()) { setError('Supplier is required'); return; }
    if (form.partyGstin?.trim() && !GSTIN_RE.test(form.partyGstin.trim())) {
      setError('Invalid Supplier GSTIN. Format: 22AAAAA0000A1Z5 (15 characters)'); return;
    }
    if (!form.items?.length) { setError('Add at least one item'); return; }
    setError(null); setSaving(true);
    try {
      const payload = { ...form, status, journalEntries: form.journalEntries ?? [] };
      if (editing) { await purchasesApi.update(editing, payload); }
      else { await purchasesApi.create(payload as Partial<PurchaseInvoice>); }
      qc.invalidateQueries({ queryKey: ['purchases'] });
      qc.refetchQueries({ type: 'all' });
      backToList();
    } catch (err) { setError(err instanceof Error ? err.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  const filteredParties = parties.filter(p => p.name.toLowerCase().includes(partySearch.toLowerCase())).slice(0, 8);
  const isCgst = form.taxType === 'cgst_sgst';

  // ── List View ────────────────────────────────────────────────────────────────
  if (mode === 'list') {
    const STATUS_CLS: Record<string, string> = { confirmed: 'badge-paid', draft: 'badge-draft', cancelled: 'badge-overdue' };
    const PAY_CLS: Record<string, string> = { paid: 'badge-paid', unpaid: 'badge-unpaid', partial: 'badge-blue' };
    const total = purchases.filter(p => p.status === 'confirmed' && p.docType !== 'debit_note').reduce((s, p) => s + p.total, 0);
    return (
      <>
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Purchase Invoices</h1>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: 'var(--text-2)', fontSize: '0.9rem' }}>Total: {fmt(total)}</span>
            <button className="btn btn-primary" onClick={openCreate}>+ New Purchase</button>
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <input value={listSearch} onChange={e => setListSearch(e.target.value)} placeholder="Search by supplier or PI #…" style={{ maxWidth: 320 }} />
        </div>
        <div className="card">
          {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
            : filtered.length === 0 ? <div className="empty-state"><div className="empty-state-icon">🛒</div><div className="empty-state-text">No purchase invoices</div></div>
            : (
              <table>
                <thead>
                  <tr><th>PI #</th><th>Doc Type</th><th>Supplier Inv.</th><th>Date</th><th>Supplier</th><th>Tax Type</th><th style={{ textAlign: 'right' }}>Total</th><th>Status</th><th>Payment</th><th style={{ width: 110 }}>Actions</th></tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id} style={p.docType === 'debit_note' ? { background: 'var(--row-tint-yellow)' } : undefined}>
                      <td style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.875rem' }}>{p.invoiceNumber}</td>
                      <td><span className={`badge ${p.docType === 'debit_note' ? 'badge-orange' : 'badge-blue'}`} style={{ fontSize: '0.7rem' }}>{p.docType === 'debit_note' ? 'Debit Note' : 'Purchase'}</span></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{p.supplierInvoice ?? '—'}</td>
                      <td>{p.date}</td>
                      <td style={{ fontWeight: 500 }}>{p.partyName}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{p.taxType === 'cgst_sgst' ? 'CGST+SGST' : p.taxType === 'igst' ? 'IGST' : 'Exempt'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: p.docType === 'debit_note' ? 'var(--danger)' : undefined }}>{p.docType === 'debit_note' ? '−' : ''}{fmt(p.total)}</td>
                      <td><span className={`badge ${STATUS_CLS[p.status] ?? 'badge-gray'}`}>{p.status}</span></td>
                      <td><span className={`badge ${PAY_CLS[p.paymentStatus ?? 'unpaid'] ?? 'badge-gray'}`}>{p.paymentStatus ?? 'unpaid'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setViewing(p)}>View</button>
                          {(p.paymentStatus ?? 'unpaid') !== 'paid' && (
                            <button className="btn btn-success btn-sm" onClick={() => setPayModal(p.id)}>+ Pay</button>
                          )}
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>Edit</button>
                          {canDelete && <button className="btn btn-danger btn-sm" disabled={del.isPending} onClick={async () => { if (await confirm({ message: `Delete ${p.invoiceNumber}?`, danger: true })) del.mutate(p.id); }}>Del</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          {purchasesMeta && purchasesMeta.totalPages > 1 && (
            <Pagination page={page} totalPages={purchasesMeta.totalPages} onChange={setPage} />
          )}
        </div>
      </div>

      {/* View Modal */}
      {viewing && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewing(null)}>
          <div className="modal" style={{ maxWidth: 780, maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <span className="modal-title">Purchase Invoice — {viewing.invoiceNumber}</span>
              <button className="modal-close" onClick={() => setViewing(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row form-row-2">
                <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem 1rem' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>Supplier</div>
                  <div style={{ fontWeight: 700 }}>{viewing.partyName}</div>
                  {viewing.partyGstin && <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', fontFamily: 'monospace' }}>GSTIN: {viewing.partyGstin}</div>}
                </div>
                <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem 1rem' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>Details</div>
                  <div style={{ fontSize: '0.875rem' }}>Date: <strong>{viewing.date}</strong></div>
                  {viewing.supplierInvoice && <div style={{ fontSize: '0.875rem' }}>Supplier Inv: <strong>{viewing.supplierInvoice}</strong></div>}
                  <div style={{ fontSize: '0.875rem' }}>Tax Type: <strong>{viewing.taxType === 'cgst_sgst' ? 'CGST+SGST' : viewing.taxType === 'igst' ? 'IGST' : 'Exempt'}</strong></div>
                  {viewing.dueDate && <div style={{ fontSize: '0.875rem' }}>Due: <strong>{viewing.dueDate}</strong></div>}
                </div>
              </div>
              <table style={{ marginBottom: '1rem' }}>
                <thead>
                  <tr>
                    <th>#</th><th>Description</th><th>HSN</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Rate</th>
                    <th style={{ textAlign: 'right' }}>Taxable</th>
                    <th style={{ textAlign: 'right' }}>GST</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewing.items ?? []).map((it, i) => (
                    <tr key={it.id ?? i}>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{i + 1}</td>
                      <td style={{ fontWeight: 500 }}>{it.description}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{it.hsnSac ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>{it.qty}</td>
                      <td style={{ textAlign: 'right' }}>₹{fmt2(it.rate)}</td>
                      <td style={{ textAlign: 'right' }}>₹{fmt2(it.taxableValue)}</td>
                      <td style={{ textAlign: 'right', fontSize: '0.85rem', color: 'var(--text-2)' }}>{it.gstRate}%</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>₹{fmt2(it.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <table style={{ width: 260 }}>
                  <tbody>
                    <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0', fontSize: '0.875rem' }}>Subtotal</td><td style={{ textAlign: 'right', fontWeight: 500 }}>₹{fmt2(viewing.subtotal ?? 0)}</td></tr>
                    <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0', fontSize: '0.875rem' }}>Tax</td><td style={{ textAlign: 'right' }}>₹{fmt2(viewing.taxTotal ?? 0)}</td></tr>
                    <tr style={{ borderTop: '2px solid var(--border)' }}>
                      <td style={{ fontWeight: 700, paddingTop: '0.4rem' }}>Total</td>
                      <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--primary)', paddingTop: '0.4rem' }}>₹{fmt2(viewing.total ?? 0)}</td>
                    </tr>
                    {(viewing.partialPaidAmount ?? 0) > 0 && (
                      <tr>
                        <td style={{ color: 'var(--success)', fontSize: '0.875rem' }}>Paid</td>
                        <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 600 }}>₹{fmt2(viewing.partialPaidAmount ?? 0)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {viewing.notes && (
                <div style={{ background: 'var(--surface-alt)', borderRadius: 6, padding: '0.65rem 0.75rem', fontSize: '0.875rem', color: 'var(--text-2)' }}>
                  <strong>Notes:</strong> {viewing.notes}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setViewing(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => { openEdit(viewing); setViewing(null); }}>Edit</button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Modal — a real, dated PurchasePayment record (mode/reference/
          notes all persisted), not an overwritten number on the invoice.
          See TRACKER.md's finding on why this used to silently discard the
          mode/reference fields the old inline form already collected. */}
      {payModal && (() => {
        const pi = purchases.find(p => p.id === payModal);
        if (!pi) return null;
        const remaining = pi.total - (pi.partialPaidAmount ?? 0);
        return (
          <RecordPurchasePaymentModal
            purchaseInvoiceId={pi.id}
            invoiceNumber={pi.invoiceNumber}
            balanceDue={Math.max(0, remaining)}
            onClose={() => setPayModal(null)}
          />
        );
      })()}
      </>
    );
  }

  // ── Create / Edit View ───────────────────────────────────────────────────────
  const subtotal = form.subtotal ?? 0;
  const taxTotal = form.taxTotal ?? 0;
  const total = form.total ?? 0;

  const isDebitNote = form.docType === 'debit_note';
  const linkedPurchaseOptions = purchases.filter(p => p.docType !== 'debit_note' && p.status === 'confirmed');
  const filteredLinked = linkedPurchaseOptions.filter(p =>
    !linkedSearch
    || p.invoiceNumber.toLowerCase().includes(linkedSearch.toLowerCase())
    || p.partyName.toLowerCase().includes(linkedSearch.toLowerCase())
    || (p.supplierInvoice ?? '').toLowerCase().includes(linkedSearch.toLowerCase())
  ).slice(0, 10);

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <div className="page-header">
        <h1 className="page-title">
          {editing ? (isDebitNote ? 'Edit Debit Note' : 'Edit Purchase Invoice') : (isDebitNote ? 'New Debit Note' : 'New Purchase Invoice')}
        </h1>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-ghost" onClick={backToList}>Cancel</button>
          <button className="btn btn-ghost" onClick={() => handleSave('draft')} disabled={saving}>Save as Draft</button>
          <button className="btn btn-primary" onClick={() => handleSave('confirmed')} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : isDebitNote ? 'Confirm Debit Note' : 'Confirm Purchase'}
          </button>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Header */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Purchase Details</span></div>
        <div className="card-body">
          <div className="form-row form-row-3" style={{ marginBottom: '0.75rem' }}>
            <label>Document Type
              <select value={form.docType ?? 'purchase_invoice'} onChange={e => setForm(f => ({ ...f, docType: e.target.value as 'purchase_invoice' | 'debit_note', linkedPurchaseId: undefined }))}>
                <option value="purchase_invoice">Purchase Invoice</option>
                <option value="debit_note">Debit Note (Purchase Return)</option>
              </select>
            </label>
            <label>Date *<input type="date" value={form.date ?? ''} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></label>
            <label>Supplier Invoice #<input value={form.supplierInvoice ?? ''} onChange={e => setForm(f => ({ ...f, supplierInvoice: e.target.value }))} placeholder="Vendor's invoice number" /></label>
          </div>
          {isDebitNote && (
            <div className="form-row form-row-2" style={{ marginBottom: '0.75rem' }}>
              <label>Against Purchase Invoice *
                <div style={{ position: 'relative' }}>
                  <input value={linkedSearch}
                    onChange={e => { setLinkedSearch(e.target.value); setShowLinkedDD(true); }}
                    onFocus={() => setShowLinkedDD(true)} onBlur={() => setTimeout(() => setShowLinkedDD(false), 200)}
                    placeholder="Search PI # or supplier to link…"
                    style={form.linkedPurchaseId ? { borderColor: 'var(--success)' } : { borderColor: 'var(--warning)' }} />
                  {showLinkedDD && filteredLinked.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 50, boxShadow: 'var(--shadow-md)', maxHeight: 220, overflowY: 'auto' }}>
                      {filteredLinked.map(p => (
                        <div key={p.id} onMouseDown={() => {
                          setForm(f => ({ ...f, linkedPurchaseId: p.id, partyId: p.partyId, partyName: p.partyName, partyGstin: p.partyGstin }));
                          setPartySearch(p.partyName);
                          setLinkedSearch(p.invoiceNumber);
                          setShowLinkedDD(false);
                        }} style={{ padding: '0.6rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}>
                          <div style={{ fontWeight: 600, fontFamily: 'monospace', color: 'var(--text)' }}>{p.invoiceNumber}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{p.partyName} — {fmt(p.total)} — {p.date}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </label>
              <label>Reference Number<input value={form.referenceNumber ?? ''} onChange={e => setForm(f => ({ ...f, referenceNumber: e.target.value }))} placeholder="Debit note / return reference" /></label>
            </div>
          )}
          <div className="form-row form-row-3">
            <label>GST Type
              <select value={form.taxType ?? 'cgst_sgst'} onChange={e => setTaxType(e.target.value as 'cgst_sgst' | 'igst' | 'exempt')}>
                <option value="cgst_sgst">CGST + SGST (Intra-state)</option>
                <option value="igst">IGST (Inter-state)</option>
                <option value="exempt">Exempt</option>
              </select>
            </label>
            <label>Supplier *
              <div style={{ position: 'relative' }}>
                <input value={partySearch} onChange={e => { setPartySearch(e.target.value); setShowPartyDD(true); setForm(f => ({ ...f, partyName: e.target.value, partyId: undefined })); }}
                  onFocus={() => setShowPartyDD(true)} onBlur={() => setTimeout(() => setShowPartyDD(false), 200)}
                  placeholder="Type to search suppliers…" readOnly={isDebitNote && !!form.linkedPurchaseId} />
                {showPartyDD && filteredParties.length > 0 && !isDebitNote && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 50, boxShadow: 'var(--shadow-md)', maxHeight: 200, overflowY: 'auto' }}>
                    {filteredParties.map(p => (
                      <div key={p.id} onMouseDown={() => selectParty(p)} style={{ padding: '0.6rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}>
                        <div style={{ fontWeight: 600, color: 'var(--text)' }}>{p.name}</div>
                        {p.gstin && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>GSTIN: {p.gstin}</div>}
                      </div>
                    ))}
                    <div onMouseDown={() => { setShowInlineParty(true); setShowPartyDD(false); setInlinePartyForm(f => ({ ...f, name: partySearch })); }}
                      style={{ padding: '0.6rem 0.75rem', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: '0.875rem', background: 'var(--surface-alt)', borderTop: filteredParties.length > 0 ? '1px solid var(--border)' : undefined }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--primary-light)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface-alt)')}>
                      + Create New Supplier{partySearch ? ` "${partySearch}"` : ''}
                    </div>
                  </div>
                )}
                {showInlineParty && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '2px solid var(--primary)', borderRadius: 8, zIndex: 60, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '1rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.75rem', color: 'var(--primary)' }}>Quick-Add Supplier</div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input value={inlinePartyForm.name} onChange={e => setInlinePartyForm(f => ({ ...f, name: e.target.value }))} placeholder="Supplier name *" autoFocus style={{ flex: 2 }} />
                      <input value={inlinePartyForm.gstin} onChange={e => setInlinePartyForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} placeholder="GSTIN (optional)" maxLength={15} style={{ flex: 1, textTransform: 'uppercase' }} />
                      <select value={inlinePartyForm.type} onChange={e => setInlinePartyForm(f => ({ ...f, type: e.target.value as 'customer' | 'vendor' | 'both' }))} style={{ flex: 1 }}>
                        <option value="vendor">Vendor</option>
                        <option value="both">Both</option>
                        <option value="customer">Customer</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button type="button" className="btn btn-ghost btn-sm" onMouseDown={() => setShowInlineParty(false)}>Cancel</button>
                      <button type="button" className="btn btn-primary btn-sm" disabled={inlinePartySaving || !inlinePartyForm.name.trim()} onMouseDown={async () => {
                        if (!inlinePartyForm.name.trim()) return;
                        setInlinePartySaving(true);
                        try {
                          const created = await partiesApi.create({ name: inlinePartyForm.name.trim(), gstin: inlinePartyForm.gstin || undefined, type: inlinePartyForm.type, country: 'India' });
                          selectParty(created as Party);
                          qc.invalidateQueries({ queryKey: ['parties'] });
                          qc.refetchQueries({ type: 'all' });
                          setShowInlineParty(false);
                          setInlinePartyForm({ name: '', gstin: '', type: 'vendor' });
                        } catch { alert('Failed to create supplier'); }
                        finally { setInlinePartySaving(false); }
                      }}>
                        {inlinePartySaving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Add Supplier'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </label>
            <label>Supplier GSTIN<input value={form.partyGstin ?? ''} onChange={e => setForm(f => ({ ...f, partyGstin: e.target.value.toUpperCase() }))} placeholder="22AAAAA0000A1Z5" maxLength={15} readOnly={isDebitNote && !!form.linkedPurchaseId} style={{ textTransform: 'uppercase' }} /></label>
          </div>
          {isDebitNote && (
            <div className="warn-box" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              Debit Note = Purchase Return. This will reverse ITC claimed on the original purchase and appear under Table 4B of GSTR-3B.
            </div>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header">
          <span style={{ fontWeight: 700 }}>Line Items</span>
          <button className="btn btn-ghost btn-sm" onClick={addItem}>+ Add Row</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Description</th>
                <th style={{ width: 80 }}>HSN/SAC</th>
                <th style={{ width: 75 }}>Qty</th>
                <th style={{ width: 100 }}>Rate</th>
                <th style={{ width: 100 }}>Taxable</th>
                <th style={{ width: 80 }}>GST %</th>
                {isCgst ? <><th style={{ width: 85 }}>CGST</th><th style={{ width: 85 }}>SGST</th></> : form.taxType === 'igst' ? <th style={{ width: 90 }}>IGST</th> : null}
                <th style={{ width: 110, textAlign: 'right' }}>Total</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {(form.items ?? []).map((item, idx) => (
                <tr key={item.id}>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{idx + 1}</td>
                  <td style={{ position: 'relative' }}>
                    <input value={item.description} onChange={e => {
                      setItem(idx, 'description', e.target.value);
                      setProdDropIdx(idx);
                    }} onFocus={() => setProdDropIdx(idx)} onBlur={() => setTimeout(() => setProdDropIdx(pi => pi === idx ? null : pi), 300)} placeholder="Item description" autoComplete="off" />
                    {prodDropIdx === idx && (
                      <div style={{ position: 'fixed', background: 'var(--surface)', border: '2px solid var(--primary)', borderRadius: 8, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: 300, maxHeight: 260, overflowY: 'auto' }}
                        ref={el => {
                          if (el) {
                            const input = el.previousElementSibling as HTMLElement;
                            if (input) {
                              const rect = input.getBoundingClientRect();
                              // Clamp so the dropdown (minWidth 300) never gets
                              // pushed past the right edge on a narrow screen —
                              // without this it silently clipped/became
                              // unreachable on phones since position:fixed
                              // elements don't force the page to scroll to reach them.
                              const left = Math.max(8, Math.min(rect.left, window.innerWidth - 308));
                              el.style.top = (rect.bottom + 4) + 'px';
                              el.style.left = left + 'px';
                            }
                          }
                        }}>
                        {products.filter(p => !item.description || p.name.toLowerCase().includes(item.description.toLowerCase())).slice(0, 10).map(p => (
                          <div key={p.id} onMouseDown={() => { fillFromProduct(idx, p); setProdDropIdx(null); }}
                            style={{ padding: '0.6rem 1rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'var(--primary-light)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text)' }}>{p.name}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                                {p.hsnSac ? `HSN: ${p.hsnSac}` : ''}{p.hsnSac && p.unit ? ' · ' : ''}{p.unit || ''}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.9rem' }}>₹{p.rate}</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>GST {p.gstRate}%</div>
                            </div>
                          </div>
                        ))}
                        {products.filter(p => !item.description || p.name.toLowerCase().includes(item.description.toLowerCase())).length === 0 && (
                          <div style={{ padding: '0.75rem 1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No products found</div>
                        )}
                        <div onMouseDown={() => { setInlineProductIdx(idx); setProdDropIdx(null); setInlineProductForm(f => ({ ...f, name: item.description })); }}
                          style={{ padding: '0.6rem 1rem', cursor: 'pointer', color: 'var(--primary)', fontWeight: 700, fontSize: '0.85rem', background: 'var(--surface-alt)', borderTop: '2px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--primary-light)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface-alt)')}>
                          <span style={{ fontSize: '1rem' }}>＋</span> Create new product{item.description ? ` "${item.description}"` : ''}
                        </div>
                      </div>
                    )}
                    {inlineProductIdx === idx && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, background: 'var(--surface)', border: '2px solid var(--primary)', borderRadius: 8, zIndex: 60, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '1rem', minWidth: 340 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.75rem', color: 'var(--primary)' }}>Quick-Add Product</div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                          <input value={inlineProductForm.name} onChange={e => setInlineProductForm(f => ({ ...f, name: e.target.value }))} placeholder="Product name *" autoFocus style={{ flex: '1 1 120px' }} />
                          <input value={inlineProductForm.hsnSac} onChange={e => setInlineProductForm(f => ({ ...f, hsnSac: e.target.value }))} placeholder="HSN/SAC" style={{ width: 90 }} />
                          <input value={inlineProductForm.unit} onChange={e => setInlineProductForm(f => ({ ...f, unit: e.target.value }))} placeholder="Unit" style={{ width: 60 }} />
                          <input type="number" value={inlineProductForm.rate} onChange={e => setInlineProductForm(f => ({ ...f, rate: +e.target.value }))} placeholder="Rate" min={0} style={{ width: 80 }} />
                          <select value={inlineProductForm.gstRate} onChange={e => setInlineProductForm(f => ({ ...f, gstRate: +e.target.value }))} style={{ width: 70 }}>
                            {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-ghost btn-sm" onMouseDown={() => setInlineProductIdx(null)}>Cancel</button>
                          <button type="button" className="btn btn-primary btn-sm" disabled={inlineProductSaving || !inlineProductForm.name.trim()} onMouseDown={async () => {
                            if (!inlineProductForm.name.trim()) return;
                            setInlineProductSaving(true);
                            try {
                              const created = await productsApi.create({ name: inlineProductForm.name.trim(), hsnSac: inlineProductForm.hsnSac || undefined, unit: inlineProductForm.unit, rate: inlineProductForm.rate, gstRate: inlineProductForm.gstRate });
                              fillFromProduct(idx, created as Product);
                              qc.invalidateQueries({ queryKey: ['products'] });
                              qc.refetchQueries({ type: 'all' });
                              setInlineProductIdx(null);
                              setInlineProductForm({ name: '', hsnSac: '', unit: 'pcs', rate: 0, gstRate: 18 });
                            } catch { alert('Failed to create product'); }
                            finally { setInlineProductSaving(false); }
                          }}>
                            {inlineProductSaving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Add Product'}
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                  <td><input value={item.hsnSac ?? ''} onChange={e => setItem(idx, 'hsnSac', e.target.value)} style={{ width: 70 }} placeholder="HSN" /></td>
                  <td><input type="number" value={item.qty} onChange={e => setItem(idx, 'qty', e.target.value)} min={0} style={{ width: 65 }} /></td>
                  <td><input type="number" value={item.rate} onChange={e => setItem(idx, 'rate', e.target.value)} min={0} style={{ width: 90 }} /></td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>₹{fmt2(item.taxableValue)}</td>
                  <td>
                    <select value={item.gstRate} onChange={e => setItem(idx, 'gstRate', e.target.value)} style={{ width: 65 }}>
                      {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </td>
                  {isCgst ? <><td style={{ textAlign: 'right' }}>₹{fmt2(item.cgst)}</td><td style={{ textAlign: 'right' }}>₹{fmt2(item.sgst)}</td></>
                    : form.taxType === 'igst' ? <td style={{ textAlign: 'right' }}>₹{fmt2(item.igst)}</td> : null}
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>₹{fmt2(item.total)}</td>
                  <td>
                    {(form.items?.length ?? 0) > 1 && (
                      <button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: '1rem', cursor: 'pointer', padding: '0 0.25rem' }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 1.25rem' }}>
          <table style={{ width: 300 }}>
            <tbody>
              <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>Subtotal</td><td style={{ textAlign: 'right', fontWeight: 500 }}>₹{fmt2(subtotal)}</td></tr>
              {isCgst ? <>
                <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>CGST</td><td style={{ textAlign: 'right' }}>₹{fmt2(taxTotal / 2)}</td></tr>
                <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>SGST</td><td style={{ textAlign: 'right' }}>₹{fmt2(taxTotal / 2)}</td></tr>
              </> : form.taxType === 'igst' ? <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>IGST</td><td style={{ textAlign: 'right' }}>₹{fmt2(taxTotal)}</td></tr> : null}
              <tr style={{ borderTop: '2px solid var(--border)' }}>
                <td style={{ fontWeight: 700, paddingTop: '0.4rem' }}>Total</td>
                <td style={{ textAlign: 'right', fontWeight: 800, fontSize: '1.1rem', color: 'var(--primary)', paddingTop: '0.4rem' }}>₹{fmt2(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* TDS Payable */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>TDS Payable (Income Tax Act 2025)</span></div>
        <div className="card-body">
          <div className="form-row form-row-3">
            <label>TDS Section
              <select value={form.tdsSection ?? ''} onChange={e => {
                const sec = TDS_SECTIONS.find(s => s.section === e.target.value);
                setForm(f => ({ ...f, tdsSection: e.target.value || undefined, tdsRate: sec ? sec.rate : f.tdsRate }));
              }}>
                <option value="">— No TDS —</option>
                {TDS_SECTIONS.map(s => <option key={s.section} value={s.section}>{s.label} ({s.rate}%)</option>)}
              </select>
            </label>
            <label>TDS Rate (%)
              <input type="number" min={0} max={100} step={0.01} value={form.tdsRate ?? ''} onChange={e => setForm(f => ({ ...f, tdsRate: parseFloat(e.target.value) || undefined }))} placeholder="Auto-filled from section" />
            </label>
            {form.tdsSection && (form.tdsRate ?? 0) > 0 && (
              <label>TDS Amount
                <input readOnly className="success-box" value={'₹' + ((subtotal * (form.tdsRate ?? 0)) / 100).toFixed(2)} style={{ fontWeight: 700, marginBottom: 0 }} />
              </label>
            )}
          </div>
          {form.tdsSection && (form.tdsRate ?? 0) > 0 && (
            <div className="success-box" style={{ padding: '0.65rem 1rem', fontSize: '0.85rem', marginBottom: 0 }}>
              Net Payable after TDS: <strong>₹{(total - (subtotal * (form.tdsRate ?? 0)) / 100).toFixed(2)}</strong>
              &nbsp;(TDS to deposit: <strong>₹{((subtotal * (form.tdsRate ?? 0)) / 100).toFixed(2)}</strong>)
            </div>
          )}
        </div>
      </div>

      {/* Payment & Notes */}
      <div className="card">
        <div className="card-header"><span style={{ fontWeight: 700 }}>Payment & Notes</span></div>
        <div className="card-body">
          <div className="form-row form-row-3">
            <label>Payment Status
              <select value={form.paymentStatus ?? 'unpaid'} onChange={e => setForm(f => ({ ...f, paymentStatus: e.target.value as 'paid' | 'unpaid' | 'partial' }))}>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
            </label>
            {form.paymentStatus === 'partial' && (
              <label>Amount Paid (₹)
                <input type="number" min={0} step={0.01} value={form.partialPaidAmount ?? ''} onChange={e => setForm(f => ({ ...f, partialPaidAmount: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
              </label>
            )}
            <label>Due Date<input type="date" value={form.dueDate ?? ''} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} /></label>
          </div>
          <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>Notes
            <textarea value={form.notes ?? ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Additional notes…" style={{ width: '100%', marginTop: '0.35rem', resize: 'vertical' }} />
          </label>
        </div>
      </div>
    </div>
  );
}
